import React, { useRef, useState } from "react";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css"; // Import Sun Editor's CSS File
import SunEditorCore from "suneditor/src/lib/core";
import { uploadApi } from "../../api/uploadApi";
import styles from "./MyHtmlEditor.module.css"; // Import CSS module
import { Box } from "@primer/react";

const defaultFonts = [
  "Arial",
  "Comic Sans MS",
  "Courier New",
  "Impact",
  "Georgia",
  "Tahoma",
  "Trebuchet MS",
  "Verdana",
];
const sortedFontOptions = [
  "Logical",
  "Salesforce Sans",
  "Garamond",
  "Sans-Serif",
  "Serif",
  "Times New Roman",
  "Helvetica",
  ...defaultFonts,
].sort();

interface IMyHtmlEditorProps {
  value: string;
  onValueChanged: (htmlValue: string) => void;
  height?: number;
}

const mentionsList = [
  'ten_hs',
  'ma_hs',
  'ngay_sinh',
  'ten_truong',
  'ten_khoi',
  'ten_he',
  'ten_lop',
  'nam_hoc',
  'gioi_tinh',
  'ngay',
  'thang',
  'nam'
];

const MyHtmlEditor: React.FC<IMyHtmlEditorProps> = (props) => {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState<string[]>([]);
  const editorRef = useRef<SunEditorCore | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { value, height, onValueChanged } = props;
  const [dropdownPosition, setDropdownPosition] = useState<any>({ top: 0, left: 0 });

  const getSunEditorInstance = (sunEditor: SunEditorCore) => {
    editorRef.current = sunEditor;
  };

  const handleChange = (content: string) => {
    onValueChanged(content);
  };

  const handleImageUploadBefore = (files: File[], info: object, uploadHandler: Function): any => {
    var file = files ? files[0] : undefined;
    if (file) {
      try {
        uploadApi.uploadImage(file).then(response => {
          if (response && response.is_success) {
            const imageUrl = response?.data?.url;
            if (imageUrl) {
              const responseResult = {
                result: [
                  {
                    url: imageUrl,
                    name: file?.name,
                    size: file?.size,
                  },
                ],
              };
              uploadHandler(responseResult);
            }
          }
        });
      } catch (error) {
        uploadHandler();
      }
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const node = range.startContainer;
        const offset = range.startOffset;

        // Chỉ xử lý nếu đang ở trong một text node
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent || "";
          // Tìm vị trí ký tự @ gần nhất trước con trỏ trong cùng node
          const atIndex = text.lastIndexOf('@', offset - 1);

          if (atIndex !== -1) {
            const charBefore = atIndex > 0 ? text[atIndex - 1] : ' ';
            const textAfterAt = text.slice(atIndex + 1, offset);

            // Chỉ hiện gợi ý nếu sau @ không có khoảng trắng và @ ở vị trí hợp lệ
            if (!textAfterAt.includes(' ') && (charBefore === ' ' || charBefore === '\u00A0' || charBefore === '\n')) {
              const query = textAfterAt.toLowerCase();
              const suggestions = mentionsList.filter((mention) =>
                mention.toLowerCase().includes(query)
              );
              setMentionSuggestions(suggestions);
              setShowMentions(suggestions.length > 0);

              if (suggestions.length > 0) {
                const rect = range.getBoundingClientRect();

                if (containerRef.current) {
                  const containerRect = containerRef.current.getBoundingClientRect();
                  setDropdownPosition({
                    top: rect.top - containerRect.top + rect.height,
                    left: rect.left - containerRect.left,
                  });
                }
              }
              return;
            }
          }
        }
      }
      setShowMentions(false);
    }
  };



  const insertMention = (mention: string) => {
    if (editorRef.current) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const node = range.startContainer;
        const offset = range.startOffset;
        const text = node.textContent || "";

        // Tìm vị trí ký tự @ gần nhất trước con trỏ
        const atIndex = text.lastIndexOf('@', offset - 1);

        if (atIndex !== -1) {
          range.setStart(node, atIndex);
          range.setEnd(node, offset);
          range.deleteContents();
        }
      }

      // Chèn tag mới với dấu ngoặc nhọn
      editorRef.current.insertHTML(`{${mention}} `);
      setShowMentions(false);
    }
  };

  return (
    <React.Fragment>
      {/* <div style={{ width: "100%" }}>
        <SunEditor
          getSunEditorInstance={getSunEditorInstance}
          setContents={value}
          onChange={handleChange}
          onImageUploadBefore={handleImageUploadBefore}
          setOptions={{
            buttonList: [
              ["undo", "redo"],
              ["font", "fontSize"],
              [
                "bold",
                "underline",
                "italic",
                "strike",
              ],
              ["fontColor", "hiliteColor"],
              ["align", "list", "lineHeight"],
              ["outdent", "indent"],
              ["table", "horizontalRule", "link", "image"],
              ["fullScreen", "showBlocks", "codeView"],
              ["removeFormat"],
            ],
            defaultTag: "div",
            height: "auto",
            showPathLabel: false,
            font: sortedFontOptions,
          }}
        />
      </div> */}
      <Box ref={containerRef} sx={{ overflowY: "auto", width: "100%", position: "relative" }}>
        <SunEditor
          height={height ? `${height}px` : "auto"}
          getSunEditorInstance={getSunEditorInstance}
          setContents={value}
          onChange={(content) => {
            handleChange(content);
            handleInput();
          }}
          onImageUploadBefore={(files, info, uploadHandler) =>
            handleImageUploadBefore(files, info, uploadHandler)
          }
          onKeyUp={() => handleInput()}
          setOptions={{
            minHeight: height ? `${height}px` : "300px",
            buttonList: [
              ["undo", "redo"],
              ["font", "fontSize"],
              ["bold", "underline", "italic", "strike", "subscript", "superscript"],
              ["fontColor", "hiliteColor"],
              ["align", "list", "lineHeight"],
              ["outdent", "indent"],
              ["table", "horizontalRule", "link", "image"],
              ["preview", "print"],
              ["removeFormat"],
            ],
            defaultTag: "div",
            height: height ? `${height}px` : "auto",
            font: sortedFontOptions,
            addTagsWhitelist: "table|td|th",
            attributesWhitelist: {
              table: "style",
              td: "style",
              th: "style",
            },
            showPathLabel: false,
            iframe: false,
          }}
        />
        {showMentions && mentionSuggestions.length > 0 && (
          <div
            className={styles.mentionDropdown}
            style={{
              top: Math.min(dropdownPosition.top, window.innerHeight - 220), // Không bị vượt màn hình
              left: dropdownPosition.left,
            }}
          >
            {mentionSuggestions.map((mention, index) => (
              <div
                key={index}
                onClick={() => insertMention(mention)}
                className={styles.mentionItem}
              >
                {mention}
              </div>
            ))}
          </div>
        )}
      </Box>

    </React.Fragment>
  );
};

export default MyHtmlEditor;
